import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, UsersIcon, CheckCircle2Icon, HeartPulseIcon, SparklesIcon, TargetIcon, ClipboardListIcon, GraduationCapIcon, MessageSquareQuoteIcon, Users2Icon, CheckSquareIcon, PieChartIcon, EyeIcon, PencilRulerIcon, SpeechIcon, RotateCcwIcon, AwardIcon, BookOpenIcon, ShieldCheckIcon, ZapIcon, CompassIcon, RepeatIcon, HandshakeIcon, TrophyIcon, MenuIcon, XIcon, QuoteIcon } from 'lucide-react';
import { Logo, Button, Section, FadeIn } from '../ui';
import { SiteFooter } from '../SiteFooter';
export function ForFacilitatorsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const nav = [{
    label: 'For Facilitators',
    href: '#hero'
  }, {
    label: 'Intelligence Engine',
    href: '#engine'
  }, {
    label: 'Intake',
    href: '#tools'
  }, {
    label: 'Reports',
    href: '#reports'
  }, {
    label: 'Pricing',
    href: '#pricing'
  }];
  return <div className="min-h-screen bg-white font-sans text-slate-800" data-id="element-2994">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-2995">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-2996">
          <Link to="/" data-id="element-2997">
            <Logo data-id="element-2998" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" data-id="element-2999">
            <ul className="flex items-center gap-8" data-id="element-3000">
              {nav.map(item => <li key={item.label} data-id="element-3001">
                  <a href={item.href} className="text-sm font-medium text-slate-600 hover:text-brand-navy transition-colors" data-id="element-3002">
                    {item.label}
                  </a>
                </li>)}
            </ul>
            <Button variant="primary" href="/designer" data-id="element-3003">
              Design Free Offsite
            </Button>
          </nav>
          <button className="lg:hidden p-2 text-slate-600" onClick={() => setMobileOpen(!mobileOpen)} data-id="element-3004">
            {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-3005" /> : <MenuIcon className="w-6 h-6" data-id="element-3006" />}
          </button>
        </div>
        {mobileOpen && <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-100 shadow-lg py-4 px-6 flex flex-col gap-4" data-id="element-3007">
            {nav.map(item => <a key={item.label} href={item.href} onClick={() => setMobileOpen(false)} className="text-base font-medium text-slate-800 py-2 border-b border-slate-50" data-id="element-3008">
                {item.label}
              </a>)}
            <Button variant="primary" className="w-full mt-2" href="/designer" data-id="element-3009">
              Design Free Offsite
            </Button>
          </div>}
      </header>

      {/* Hero */}
      <section id="hero" className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden bg-white" data-id="element-3010">
        {/* Animated grid pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.4]" style={{
        backgroundImage: 'linear-gradient(to right, rgb(15 23 42 / 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgb(15 23 42 / 0.05) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
      }} data-id="element-3011" />

        {/* Floating ambient orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-3012">
          <motion.div animate={{
          x: [0, 30, 0],
          y: [0, -20, 0]
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.07] rounded-full blur-3xl" data-id="element-3013" />
          <motion.div animate={{
          x: [0, -25, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 14,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.06] rounded-full blur-3xl" data-id="element-3014" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-3015">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-3016">
            <div className="lg:col-span-6" data-id="element-3017">
              <FadeIn data-id="element-3018">
                <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-4 py-1 mb-8 shadow-sm relative overflow-hidden" data-id="element-3019">
                  <span className="bg-brand-teal/10 text-brand-teal text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider relative z-10" data-id="element-3020">
                    For Facilitators
                  </span>
                  <span className="text-sm font-medium text-slate-600 relative z-10" data-id="element-3021">
                    Coaches, educators, retreat leaders
                  </span>
                  <motion.span aria-hidden initial={{
                  x: '-120%'
                }} animate={{
                  x: '320%'
                }} transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: 'easeInOut'
                }} className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent skew-x-12 pointer-events-none" data-id="element-3022" />
                </div>
              </FadeIn>

              <FadeIn delay={0.05} data-id="element-3023">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-balance mb-6 text-brand-navy" data-id="element-3024">
                  Make every retreat feel more{' '}
                  <span className="relative inline-block whitespace-nowrap" data-id="element-3025">
                    <span className="relative z-10" data-id="element-3026">personalized</span>
                    <motion.span initial={{
                    scaleX: 0
                  }} animate={{
                    scaleX: 1
                  }} transition={{
                    duration: 0.9,
                    delay: 0.6,
                    ease: [0.65, 0, 0.35, 1]
                  }} style={{
                    transformOrigin: 'left'
                  }} className="absolute bottom-1 left-0 right-0 h-3 bg-brand-coral/25 -z-0" data-id="element-3027" />
                  </span>{' '}
                  and measurable.
                </h1>
              </FadeIn>

              <FadeIn delay={0.15} data-id="element-3028">
                <p className="text-lg md:text-xl text-slate-600 mb-10 leading-relaxed max-w-xl" data-id="element-3029">
                  Offsite+ helps facilitators collect participant context,
                  design tailored learning pathways, guide reflection, create
                  group exercises, and deliver polished post-retreat reports.
                </p>
              </FadeIn>

              <FadeIn delay={0.25} data-id="element-3030">
                <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-3031">
                  <Button variant="primary" size="lg" href="/designer" className="group" data-id="element-3032">
                    Design Your Free Offsite
                    <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3033" />
                  </Button>
                  <Button variant="outline" size="lg" data-id="element-3034">
                    Book Facilitator Demo
                  </Button>
                </div>
              </FadeIn>

              <FadeIn delay={0.35} data-id="element-3035">
                <div className="border-t border-slate-100 pt-8" data-id="element-3036">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4" data-id="element-3037">
                    Built for independent practitioners
                  </p>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-slate-500 text-sm font-medium" data-id="element-3038">
                    {['Leadership coaches', 'Executive educators', 'Retreat leaders', 'Wellness facilitators', 'Consultants'].map(b => <span key={b} className="flex items-center gap-1.5" data-id="element-3039">
                        <CheckCircle2Icon className="w-3.5 h-3.5 text-brand-teal" data-id="element-3040" />
                        {b}
                      </span>)}
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Facilitator dashboard */}
            <FadeIn delay={0.4} className="lg:col-span-6 relative lg:h-[640px] flex items-center justify-center" data-id="element-3041">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 via-transparent to-brand-coral/10 rounded-full blur-3xl" data-id="element-3042" />

              {/* Decorative orbital ring */}
              <motion.div aria-hidden animate={{
              rotate: 360
            }} transition={{
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }} className="hidden lg:block absolute w-[520px] h-[520px] rounded-full border border-dashed border-slate-200/80" data-id="element-3043">
                <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-brand-teal shadow-[0_0_0_4px_rgba(14,165,164,0.15)]" data-id="element-3044" />
                <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2 h-2 rounded-full bg-brand-coral" data-id="element-3045" />
              </motion.div>

              {/* Floating accent — top left */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: -10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.1,
              duration: 0.6
            }} className="hidden md:block absolute top-8 -left-2 lg:left-4 z-20" data-id="element-3046">
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-white rounded-xl shadow-xl shadow-slate-300/40 border border-slate-100 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-3047">
                  <div className="bg-emerald-50 p-1.5 rounded-md" data-id="element-3048">
                    <TargetIcon className="w-4 h-4 text-emerald-600" data-id="element-3049" />
                  </div>
                  <div data-id="element-3050">
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3051">
                      Theme Match
                    </div>
                    <div className="text-sm font-bold text-brand-navy" data-id="element-3052">94%</div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Floating accent — bottom right */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: 10
            }} animate={{
              opacity: 1,
              y: 0,
              x: 0
            }} transition={{
              delay: 1.3,
              duration: 0.6
            }} className="hidden md:block absolute bottom-10 -right-2 lg:right-4 z-20" data-id="element-3053">
                <motion.div animate={{
                y: [0, 8, 0]
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="bg-brand-navy text-white rounded-xl shadow-xl shadow-slate-900/20 px-3.5 py-2.5 flex items-center gap-2.5" data-id="element-3054">
                  <div className="bg-brand-teal/20 p-1.5 rounded-md" data-id="element-3055">
                    <PieChartIcon className="w-4 h-4 text-brand-teal" data-id="element-3056" />
                  </div>
                  <div data-id="element-3057">
                    <div className="text-[10px] font-semibold text-brand-teal uppercase tracking-wider" data-id="element-3058">
                      Client Report
                    </div>
                    <div className="text-sm font-bold" data-id="element-3059">Ready</div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div initial={{
              y: 40,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              duration: 0.8
            }} whileHover={{
              y: -4
            }} className="relative w-full max-w-lg mx-auto bg-white rounded-2xl shadow-2xl shadow-slate-300/40 border border-slate-100 overflow-hidden z-10 transition-shadow hover:shadow-slate-400/40" data-id="element-3060">
                <div className="bg-brand-navy text-white p-5 relative overflow-hidden" data-id="element-3061">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/10 rounded-full blur-2xl" data-id="element-3062" />
                  <div className="relative z-10 flex justify-between items-start" data-id="element-3063">
                    <div data-id="element-3064">
                      <div className="flex items-center gap-2 mb-1.5" data-id="element-3065">
                        <span className="bg-brand-teal/15 text-teal-300 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-3066">
                          Facilitator Workspace
                        </span>
                      </div>
                      <h3 className="text-xl font-bold" data-id="element-3067">Leadership Retreat</h3>
                      <p className="text-xs text-slate-300 mt-1" data-id="element-3068">
                        Northstar Group · 3-day program
                      </p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-2 text-center min-w-[60px]" data-id="element-3069">
                      <UsersIcon className="w-5 h-5 mx-auto mb-1 text-brand-coral" data-id="element-3070" />
                      <span className="text-sm font-bold" data-id="element-3071">36</span>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4 bg-slate-50/50" data-id="element-3072">
                  {/* Top Themes */}
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-3073">
                    <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700" data-id="element-3074">
                      <TargetIcon className="w-4 h-4 text-brand-teal" data-id="element-3075" />
                      Top Themes from Intake
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-3076">
                      {['Trust', 'Energy', 'Communication'].map(t => <span key={t} className="bg-brand-navy/5 text-brand-navy text-xs px-2.5 py-1 rounded-md font-semibold" data-id="element-3077">
                          #{t}
                        </span>)}
                    </div>
                  </div>

                  {/* Recommended Modules */}
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm" data-id="element-3078">
                    <div className="flex items-center justify-between mb-3 text-sm font-semibold text-slate-700" data-id="element-3079">
                      <span className="flex items-center gap-2" data-id="element-3080">
                        <BookOpenIcon className="w-4 h-4 text-brand-coral" data-id="element-3081" />
                        Recommended Modules
                      </span>
                      <span className="text-xs text-brand-teal font-bold" data-id="element-3082">
                        4 selected
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-3083">
                      {[{
                      name: 'Team Trust',
                      match: 94
                    }, {
                      name: 'Communication Under Pressure',
                      match: 88
                    }, {
                      name: 'Energy & Recovery',
                      match: 81
                    }].map(m => <div key={m.name} className="flex items-center justify-between text-xs" data-id="element-3084">
                          <span className="font-medium text-slate-700" data-id="element-3085">
                            {m.name}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-3086">
                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-3087">
                              <div className="h-1.5 bg-brand-teal rounded-full" style={{
                            width: `${m.match}%`
                          }} data-id="element-3088"></div>
                            </div>
                            <span className="font-bold text-brand-teal w-8 text-right" data-id="element-3089">
                              {m.match}%
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Group Exercises */}
                  <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-3090">
                    <div className="flex items-center gap-3" data-id="element-3091">
                      <div className="bg-brand-coral/10 p-2 rounded-md" data-id="element-3092">
                        <Users2Icon className="w-4 h-4 text-brand-coral" data-id="element-3093" />
                      </div>
                      <span className="text-sm font-medium text-slate-700" data-id="element-3094">
                        Group Exercises
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded-full" data-id="element-3095">
                      6 ready
                    </span>
                  </div>

                  {/* Report Preview */}
                  <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-100 shadow-sm" data-id="element-3096">
                    <div className="flex items-center gap-3" data-id="element-3097">
                      <div className="bg-brand-navy/10 p-2 rounded-md" data-id="element-3098">
                        <PieChartIcon className="w-4 h-4 text-brand-navy" data-id="element-3099" />
                      </div>
                      <span className="text-sm font-medium text-slate-700" data-id="element-3100">
                        Report Preview
                      </span>
                    </div>
                    <span className="flex items-center text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full" data-id="element-3101">
                      <CheckCircle2Icon className="w-3 h-3 mr-1" data-id="element-3102" /> Ready
                    </span>
                  </div>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Problem */}
      <Section className="bg-slate-50" data-id="element-3103">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3104">
          <FadeIn data-id="element-3105">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3106">
              Great facilitation needs better context.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-3107">
              The work happens in the room — but the leverage comes from
              everything around it.
            </p>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3108">
          {['Participants arrive with hidden needs', 'Intake is often too shallow', 'Content is hard to personalize', 'Reflection gets lost after the event', 'Clients want proof of value', 'Reports take too long to create manually'].map((p, i) => <FadeIn key={p} delay={i * 0.05} data-id="element-3109">
              <div className="bg-white p-6 rounded-2xl border border-slate-100 h-full flex items-start gap-3 shadow-sm" data-id="element-3110">
                <div className="w-2 h-2 rounded-full bg-brand-coral mt-2 shrink-0" data-id="element-3111" />
                <p className="text-slate-700 font-medium" data-id="element-3112">{p}</p>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Before / After Transformation */}
      <Section className="bg-white border-y border-slate-100" data-id="element-3113">
        <div className="max-w-5xl mx-auto" data-id="element-3114">
          <FadeIn className="text-center mb-12" data-id="element-3115">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3116">
              From cold rooms to prepared cohorts.
            </h2>
            <p className="text-lg text-slate-600" data-id="element-3117">
              Give yourself the context you need to do your best work.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-3118">
            {/* Before */}
            <FadeIn delay={0.1} data-id="element-3119">
              <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200 h-full" data-id="element-3120">
                <div className="flex items-center gap-3 mb-6" data-id="element-3121">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center" data-id="element-3122">
                    <XIcon className="w-5 h-5 text-slate-500" data-id="element-3123" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-700" data-id="element-3124">
                    Cold Room
                  </h3>
                </div>
                <ul className="space-y-4" data-id="element-3125">
                  {['You meet the group for the first time onsite', 'Generic intake forms with low completion', 'One-size-fits-all agenda', 'Post-it notes get lost after the session', 'No way to prove the impact to the sponsor'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-600" data-id="element-3126">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 shrink-0" data-id="element-3127" />
                      <span data-id="element-3128">{item}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            {/* After */}
            <FadeIn delay={0.2} data-id="element-3129">
              <div className="bg-brand-navy rounded-3xl p-8 border border-brand-navy shadow-xl h-full relative overflow-hidden" data-id="element-3130">
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand-teal/10 rounded-full blur-3xl" data-id="element-3131" />
                <div className="relative z-10" data-id="element-3132">
                  <div className="flex items-center gap-3 mb-6" data-id="element-3133">
                    <div className="w-10 h-10 rounded-full bg-brand-teal flex items-center justify-center" data-id="element-3134">
                      <CheckCircle2Icon className="w-5 h-5 text-white" data-id="element-3135" />
                    </div>
                    <h3 className="text-xl font-bold text-white" data-id="element-3136">
                      Prepared Cohort
                    </h3>
                  </div>
                  <ul className="space-y-4" data-id="element-3137">
                    {['Deep context on team dynamics before arrival', 'Branded intake with 90%+ completion', 'AI-tailored learning pathways', 'Digital reflection and commitment capture', 'Polished executive report ready instantly'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-300" data-id="element-3138">
                        <CheckCircle2Icon className="w-5 h-5 text-brand-teal shrink-0" data-id="element-3139" />
                        <span data-id="element-3140">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Solution */}
      <Section className="bg-slate-50" data-id="element-3141">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3142">
          <FadeIn data-id="element-3143">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3144">
              Offsite+ gives facilitators the structure around the session.
            </h2>
          </FadeIn>
        </div>
        <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-3145">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-slate-200 z-0" data-id="element-3146"></div>
          {[{
          title: 'Before',
          color: 'text-brand-teal',
          bg: 'bg-brand-teal/10',
          items: ['Participant intake', 'Goals and themes', 'Readiness signals', 'Learning pathway design']
        }, {
          title: 'During',
          color: 'text-brand-coral',
          bg: 'bg-brand-coral/10',
          items: ['Prompts', 'Group exercises', 'Reflection cards', 'Commitment capture']
        }, {
          title: 'After',
          color: 'text-brand-navy',
          bg: 'bg-brand-navy/10',
          items: ['Feedback', 'Themes', 'Outcomes', 'Client report']
        }].map((c, i) => <FadeIn key={c.title} delay={i * 0.15} className="relative z-10" data-id="element-3147">
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-slate-100 h-full" data-id="element-3148">
                <div className={`${c.bg} ${c.color} w-12 h-12 rounded-xl mb-5 flex items-center justify-center font-bold`} data-id="element-3149">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-5" data-id="element-3150">
                  {c.title}
                </h3>
                <ul className="space-y-3" data-id="element-3151">
                  {c.items.map(it => <li key={it} className="flex items-center gap-2 text-slate-700 font-medium text-sm" data-id="element-3152">
                      <CheckCircle2Icon className={`w-4 h-4 ${c.color} shrink-0`} data-id="element-3153" />
                      {it}
                    </li>)}
                </ul>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Intelligence Engine */}
      <Section id="engine" className="bg-brand-navy text-white relative overflow-hidden" data-id="element-3154">
        <div className="absolute inset-0 pointer-events-none" data-id="element-3155">
          <div className="absolute -top-20 right-0 w-[500px] h-[500px] bg-brand-teal/10 rounded-full blur-3xl" data-id="element-3156" />
          <div className="absolute -bottom-20 left-0 w-[500px] h-[500px] bg-brand-coral/10 rounded-full blur-3xl" data-id="element-3157" />
        </div>
        <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10" data-id="element-3158">
          <FadeIn data-id="element-3159">
            <div className="inline-flex items-center gap-2 bg-brand-coral/10 text-brand-coral text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-6" data-id="element-3160">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-3161" />
              Intelligence Engine
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-3162">
              Your retreat design co-pilot.
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8" data-id="element-3163">
              The Offsite+ Intelligence Engine combines AI, proven leadership
              frameworks, wellness concepts, and executive-education style
              thinking to generate learning pathways, prompts, exercises, and
              follow-up plans.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-3164">
            <div className="space-y-4" data-id="element-3165">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-3166">
                Sample AI Prompts Generated
              </div>
              {[{
              type: 'Pair Share',
              text: "When did you last feel completely aligned with the team's direction?"
            }, {
              type: 'Group Reflection',
              text: 'What is one unspoken assumption holding this project back?'
            }, {
              type: 'Commitment',
              text: 'What is one habit you will change next week to support team trust?'
            }].map((prompt, i) => <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm" data-id="element-3167">
                  <div className="text-[10px] font-bold text-brand-teal uppercase tracking-wider mb-2" data-id="element-3168">
                    {prompt.type}
                  </div>
                  <div className="text-sm font-medium text-white leading-relaxed" data-id="element-3169">
                    "{prompt.text}"
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Trusted Frameworks */}
      <Section className="bg-white border-b border-slate-100" data-id="element-3170">
        <div className="max-w-5xl mx-auto text-center" data-id="element-3171">
          <FadeIn data-id="element-3172">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-navy mb-4" data-id="element-3173">
              Trusted frameworks you can stand behind.
            </h2>
            <p className="text-lg text-slate-600 mb-10" data-id="element-3174">
              Integrate the methodologies you already know and love into your
              pathways.
            </p>
            <div className="flex flex-wrap justify-center gap-3" data-id="element-3175">
              {["Lencioni's Five Dysfunctions", 'Radical Candor', 'The Five Behaviors', 'Immunity to Change', 'Getting Things Done (GTD)', 'Nonviolent Communication'].map((fw, i) => <motion.div key={fw} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.05
            }} className="bg-slate-50 border border-slate-200 text-slate-700 font-semibold px-4 py-2 rounded-full text-sm shadow-sm" data-id="element-3176">
                  {fw}
                </motion.div>)}
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* World-Class Content Partners */}
      <Section className="bg-slate-50 border-b border-slate-100 py-16" data-id="element-3177">
        <div className="max-w-5xl mx-auto text-center" data-id="element-3178">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1 text-xs font-bold text-brand-navy uppercase tracking-wider mb-6 shadow-sm" data-id="element-3179">
            <SparklesIcon className="w-3.5 h-3.5 text-brand-coral" data-id="element-3180" />
            World-Class Content
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-brand-navy mb-8" data-id="element-3181">
            Every pathway is built from licensed and adapted material from top
            business schools and bestselling authors.
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-6 opacity-70 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-3182">
            {['Harvard Business School', 'Wharton', 'INSEAD', 'Stanford GSB', 'MIT Sloan', 'Kellogg'].map(src => <div key={src} className="font-serif text-lg md:text-xl font-medium text-brand-navy" data-id="element-3183">
                {src}
              </div>)}
          </div>
        </div>
      </Section>

      {/* White-glove for your practice */}
      <Section className="bg-white" data-id="element-3184">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-3185">
          <FadeIn data-id="element-3186">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-3187">
              A white-label experience for your clients.
            </h2>
            <p className="text-lg text-slate-600 mb-8" data-id="element-3188">
              Your logo, your brand colors, your methodology. Offsite+ runs
              quietly in the background, making you look like a premium,
              tech-enabled practice.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-3189">
              {['Custom domain mapping', 'Branded client event hubs', 'Your logo on all reports', 'Custom intake questions'].map((item, i) => <li key={i} className="flex items-center gap-3 text-slate-700 font-medium" data-id="element-3190">
                  <CheckCircle2Icon className="w-5 h-5 text-brand-teal" data-id="element-3191" />
                  {item}
                </li>)}
            </ul>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-3192">
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-2 shadow-inner" data-id="element-3193">
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3194">
                <div className="bg-slate-800 px-4 py-3 flex items-center gap-3" data-id="element-3195">
                  <div className="w-8 h-8 rounded bg-white flex items-center justify-center font-bold text-slate-800 text-xs" data-id="element-3196">
                    LOGO
                  </div>
                  <div className="text-white font-medium text-sm" data-id="element-3197">
                    Your Practice Name
                  </div>
                </div>
                <div className="p-6" data-id="element-3198">
                  <div className="w-3/4 h-6 bg-slate-100 rounded mb-4" data-id="element-3199" />
                  <div className="w-1/2 h-4 bg-slate-100 rounded mb-8" data-id="element-3200" />
                  <div className="grid grid-cols-2 gap-4" data-id="element-3201">
                    <div className="h-24 bg-slate-50 rounded border border-slate-100" data-id="element-3202" />
                    <div className="h-24 bg-slate-50 rounded border border-slate-100" data-id="element-3203" />
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Testimonial */}
      <Section className="bg-slate-50" data-id="element-3204">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3205">
          <FadeIn data-id="element-3206">
            <QuoteIcon className="w-12 h-12 text-brand-teal/20 mx-auto mb-6" data-id="element-3207" />
            <h3 className="text-2xl md:text-3xl font-medium text-brand-navy leading-relaxed mb-8" data-id="element-3208">
              "Offsite+ completely changed how I run my practice. I spend less
              time on logistics and more time on the actual coaching. The
              post-retreat reports alone have helped me win three new retainer
              clients this year."
            </h3>
            <div className="flex items-center justify-center gap-4" data-id="element-3209">
              <div className="w-12 h-12 rounded-full bg-slate-200 overflow-hidden" data-id="element-3210">
                <img src="https://i.pravatar.cc/150?u=fac" alt="Marcus Thorne" className="w-full h-full object-cover" data-id="element-3211" />
              </div>
              <div className="text-left" data-id="element-3212">
                <div className="font-bold text-brand-navy" data-id="element-3213">Marcus Thorne</div>
                <div className="text-sm text-slate-500" data-id="element-3214">
                  Independent Leadership Coach
                </div>
              </div>
              <div className="h-8 w-px bg-slate-200 mx-4" data-id="element-3215" />
              <div className="text-left" data-id="element-3216">
                <div className="text-lg font-bold text-brand-teal" data-id="element-3217">14</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-3218">
                  Retreats / Year
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Pricing */}
      <Section id="pricing" className="bg-white" data-id="element-3219">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3220">
          <FadeIn data-id="element-3221">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3222">
              Pricing for solo facilitators and growing practices.
            </h2>
          </FadeIn>
        </div>
        <div className="flex flex-wrap justify-center gap-5 max-w-7xl mx-auto" data-id="element-3223">
          {[{
          name: 'Free',
          price: '$0',
          period: '',
          desc: 'Plan a retreat in minutes.',
          cta: 'Design Free Offsite',
          href: '/designer',
          highlight: false,
          features: ['AI Offsite Designer', 'Custom agenda outline', 'Reflection prompts', 'Intake suggestions']
        }, {
          name: 'Starter',
          price: '$499',
          period: '/ event',
          desc: 'Run small training days and retreats.',
          cta: 'Start an Event',
          highlight: false,
          features: ['Branded intake form', 'Basic event hub', 'Up to 50 participants', 'Simple post-event summary']
        }, {
          name: 'Professional',
          price: '$1,500',
          period: '/ event',
          desc: 'For most facilitator-led retreats.',
          cta: 'Launch Retreat',
          highlight: true,
          features: ['Up to 150 participants', 'AI agenda + prompts', 'Wellness preferences', 'Small group matching', 'Engagement dashboard', 'Client-ready report']
        }, {
          name: 'Premium',
          price: '$3,500',
          period: '/ event',
          desc: 'Premium executive retreats.',
          cta: 'Build Premium',
          highlight: false,
          features: ['Up to 300 participants', 'Custom learning modules', 'Facilitator guide', '30-day accountability flow', 'White-glove setup']
        }, {
          name: 'Facilitator Partner',
          price: 'Custom',
          period: 'annual',
          desc: 'Run multiple retreats per year.',
          cta: 'Become a Partner',
          highlight: false,
          features: ['White-label branding', 'Multiple events', 'Reusable templates', 'Practice dashboard', 'Priority support']
        }].map((plan, i) => <FadeIn key={plan.name} delay={i * 0.08} className={`flex-1 min-w-[260px] max-w-[320px] flex flex-col ${plan.highlight ? 'md:-mt-3 md:mb-3' : ''}`} data-id="element-3224">
              <div className={`relative h-full flex flex-col bg-white rounded-2xl p-6 ${plan.highlight ? 'border-2 border-brand-coral shadow-lg scale-105 z-10' : 'border border-slate-200 shadow-sm'}`} data-id="element-3225">
                {plan.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-coral text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-3226">
                    Recommended
                  </div>}
                <h3 className="text-lg font-bold text-brand-navy mb-1" data-id="element-3227">
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-2" data-id="element-3228">
                  <span className="text-2xl font-black text-slate-800" data-id="element-3229">
                    {plan.price}
                  </span>
                  {plan.period && <span className="text-xs text-slate-500 font-medium" data-id="element-3230">
                      {plan.period}
                    </span>}
                </div>
                <p className="text-xs text-slate-600 mb-5 h-8" data-id="element-3231">{plan.desc}</p>
                <ul className="space-y-2 mb-6 flex-1" data-id="element-3232">
                  {plan.features.map(f => <li key={f} className="flex items-start gap-2 text-xs text-slate-700" data-id="element-3233">
                      <CheckCircle2Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${plan.highlight ? 'text-brand-coral' : 'text-brand-teal'}`} data-id="element-3234" />
                      <span data-id="element-3235">{f}</span>
                    </li>)}
                </ul>
                <Button variant={plan.highlight ? 'accent' : plan.name === 'Free' ? 'outline' : 'primary'} className="w-full" size="sm" href={plan.href} data-id="element-3236">
                  {plan.cta}
                </Button>
              </div>
            </FadeIn>)}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="bg-brand-navy text-white relative overflow-hidden" data-id="element-3237">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-3238">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-brand-teal/20 rounded-full blur-[120px]" data-id="element-3239" />
          <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] bg-brand-coral/20 rounded-full blur-[120px]" data-id="element-3240" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-3241">
          <FadeIn data-id="element-3242">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-balance text-white" data-id="element-3243">
              Bring more structure, personalization, and proof to your next
              retreat.
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10" data-id="element-3244">
              <Button variant="accent" size="lg" href="/designer" data-id="element-3245">
                Design Free Offsite
              </Button>
              <Button variant="outline" size="lg" className="border-white/20 text-white hover:bg-white/10 hover:border-white/40" data-id="element-3246">
                Book Facilitator Demo
              </Button>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-10" data-id="element-3247">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-3248">
          <Logo data-id="element-3249" />
          <div className="flex gap-6 text-sm font-medium text-slate-500" data-id="element-3250">
            <Link to="/" className="hover:text-brand-navy transition-colors" data-id="element-3251">
              Home
            </Link>
            <Link to="/designer" className="hover:text-brand-navy transition-colors" data-id="element-3252">
              AI Designer
            </Link>
            <Link to="/report" className="hover:text-brand-navy transition-colors" data-id="element-3253">
              Sample Report
            </Link>
          </div>
          <div className="text-sm text-slate-400" data-id="element-3254">
            © {new Date().getFullYear()} Offsite+
          </div>
        </div>
      </footer>
    </div>;
}